import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Crown, ShieldCheck, Briefcase, Users as UsersIcon, Ban, UserPlus, X } from "lucide-react";
import {
  getParticipants, getSubPromoters, getBusinessAssistants, sendHostRequest, cancelHostRequest, getBlockedUsers, unblockUser,
  bulkRemoveBusinessAssistants, bulkBlockKickParticipants, bulkUnblockUsers,
} from "../../services/eventDetailService";
import Loader from "../common/Loader";
import Button from "../common/Button";
import Card from "../common/Card";
import Badge from "../common/Badge";
import Checkbox from "../common/Checkbox";
import EmptyState from "../common/EmptyState";
import ConfirmModal from "../common/ConfirmModal";
import RemoveParticipantModal from "./RemoveParticipantModal";
import SubPromoterStatusModal from "./SubPromoterStatusModal";
import SubPromoterQrPermissionModal from "./SubPromoterQrPermissionModal";
import InviteBusinessAssistantModal from "./InviteBusinessAssistantModal";
import EditPermissionsModal from "./EditPermissionsModal";
import EditAccessContactPermissionsModal from "./EditAccessContactPermissionsModal";
import { color, radius, shadow } from "../../styles/theme";
import { extractListResult } from "../../utils/apiHelpers";

const getUserImage = (user) =>
  user?.AppUser?.BusinessDetails?.LogoUrl || user?.LogoUrl || user?.DisplayPicture || null;

const getDisplayName = (user, fallback) =>
  user?.BusinessNickName || user?.AssistantName || user?.AppUser?.BusinessDetails?.BusinessNickName || user?.NickName || fallback;

const Avatar = ({ src, name }) => (
  src ? (
    <img src={src} alt="" style={s.avatar} />
  ) : (
    <div style={s.avatarFallback}>{(name || "?").charAt(0).toUpperCase()}</div>
  )
);

// Real backend enum (WebAPI/Enums/Enums.cs SubPromoterStatus): 1=Active,
// 2=NonPublic, 3=Deactivated, 4=Pending. 2 was previously mislabeled "pending"
// here (Pending is actually 4, which this UI never sets) — see
// SubPromoterStatusModal.jsx for the matching fix on the status-change side.
// 4 is kept mapped defensively for any pre-existing record, even though nothing
// in this panel sets it anymore.
const SUB_PROMOTER_STATUS_VARIANT = { 1: "success", 2: "info", 3: "danger", 4: "warning" };
const SUB_PROMOTER_STATUS_KEY = { 1: "active", 2: "nonPublic", 3: "deactivated", 4: "pending" };

/**
 * Participants tab — Admin/Owner, Event Hosts, Sub-Promoters, Business
 * Assistants, Participants (paginated). Ported from mobile's
 * participants-tab/index.js + event-admin/event-hosts/event-sub-promoters/
 * event-business-assistant/event-participants, condensed into one file
 * since each mobile sub-component is small.
 */
const ParticipantsTab = ({ event, eventId, isAdmin, permissions }) => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const chatRoomUsers = event?.ChatRoom?.ChatRoomUsers ?? [];
  const adminUser = chatRoomUsers.find((u) => u?.UserId === event?.UserId)?.AppUser ?? null;
  const eventHosts = chatRoomUsers.filter((u) => u?.IsAccessContact);
  const isPrivateEvent = event?.Origin === "Private";
  const hostNoun = isPrivateEvent ? t("participantsTab.hostNoun.helper") : t("participantsTab.hostNoun.host");
  // Event Host only applies to Non-Public events — a Public event's
  // participants must never be offered "Make Host" (see eventPermissions.js's
  // isNonPublicEvent).
  const canMakeHost = Boolean(permissions?.isNonPublicEvent);
  // Business Assistant is a Public-event-only concept tied to a business
  // account — Non-Public and Private events use Event Host/AccessContact
  // instead (the eventHosts section above, which must stay untouched), and
  // neither applies to Family/@Room events. Mirrors SettingsTab.jsx's
  // canInviteBA gate so "can this event have a BA at all" stays one rule.
  const canUseBusinessAssistants =
    Boolean(permissions?.isPublicEvent) && !permissions?.isFamilyEvent && !permissions?.isARoomEvent;

  // Event Hosts (above) comes from the event's already-embedded ChatRoomUsers
  // — cheap, and always complete regardless of the Participants list's
  // pagination. It doesn't carry AccessContactRequestStatus though (that's
  // only on the paginated /EventUsers participant records), so the
  // Permissions action needs this small side-fetch to resolve each host's
  // request id. Hosts are rare, so pageSize is set high enough to get them
  // all in one page rather than paginating this too.
  const [hostRequestStatuses, setHostRequestStatuses] = useState([]);

  const [subPromoters, setSubPromoters] = useState([]);
  const [loadingSubPromoters, setLoadingSubPromoters] = useState(true);
  const [businessAssistants, setBusinessAssistants] = useState([]);
  const [loadingBA, setLoadingBA] = useState(true);
  const [participants, setParticipants] = useState([]);
  const [participantsMeta, setParticipantsMeta] = useState(null);
  const [participantsPage, setParticipantsPage] = useState(1);
  const [loadingParticipants, setLoadingParticipants] = useState(true);

  const [removeTarget, setRemoveTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [qrPermissionTarget, setQrPermissionTarget] = useState(null);
  const [showInviteBA, setShowInviteBA] = useState(false);
  const [permissionsTarget, setPermissionsTarget] = useState(null);
  const [accessContactPermissionsTarget, setAccessContactPermissionsTarget] = useState(null);
  const [hostTarget, setHostTarget] = useState(null); // { mode: 'make'|'cancel', userId, requestId, name }
  const [hostActionLoading, setHostActionLoading] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [blockedMeta, setBlockedMeta] = useState(null);
  const [blockedPage, setBlockedPage] = useState(1);
  const [loadingBlocked, setLoadingBlocked] = useState(true);
  const [unblockingId, setUnblockingId] = useState(null);

  // Bulk-select — one section "active" at a time (following GalleryDetail.jsx's
  // id-map selection pattern), since each section's rows are a different
  // entity with a different bulk action.
  const [selection, setSelection] = useState({ section: null, ids: {} });
  const [bulkBusy, setBulkBusy] = useState(false);
  const selectedIds = Object.keys(selection.ids);

  const toggleSelect = (section, id) => {
    setSelection((prev) => {
      if (prev.section !== section) return { section, ids: { [id]: true } };
      const ids = { ...prev.ids };
      if (ids[id]) delete ids[id];
      else ids[id] = true;
      return { section, ids };
    });
  };
  const clearSelection = () => setSelection({ section: null, ids: {} });

  const loadSubPromoters = () => {
    setLoadingSubPromoters(true);
    getSubPromoters(eventId)
      .then((res) => setSubPromoters(extractListResult(res.data).filter((u) => (u?.AppUser?.Id ?? u?.Id) !== event?.UserId)))
      .catch(() => toast.error(t("participantsTab.loadSubPromotersError")))
      .finally(() => setLoadingSubPromoters(false));
  };

  const loadHostRequestStatuses = () => {
    if (eventHosts.length === 0) {
      setHostRequestStatuses([]);
      return;
    }
    getParticipants(eventId, { pageNumber: 1, pageSize: 1000 })
      .then((res) => setHostRequestStatuses(extractListResult(res.data).filter((p) => p?.IsAccessContact === true)))
      .catch(() => {}); // Permissions button just stays hidden for these rows if this fails
  };

  const loadBusinessAssistants = () => {
    setLoadingBA(true);
    getBusinessAssistants(eventId, { pageNumber: 1, pageSize: 5 })
      .then((res) => setBusinessAssistants(extractListResult(res.data)))
      .catch(() => toast.error(t("participantsTab.loadAssistantsError")))
      .finally(() => setLoadingBA(false));
  };

  const loadParticipants = (page = 1) => {
    setLoadingParticipants(true);
    getParticipants(eventId, { pageNumber: page, pageSize: 5 })
      .then((res) => {
        const newData = extractListResult(res.data);
        setParticipants((prev) => (page === 1 ? newData : [...prev, ...newData]));
        setParticipantsMeta(res.data?.Metadata || null);
        setParticipantsPage(page);
      })
      .catch(() => toast.error(t("participantsTab.loadParticipantsError")))
      .finally(() => setLoadingParticipants(false));
  };

  const loadBlockedUsers = (page = 1) => {
    setLoadingBlocked(true);
    getBlockedUsers(eventId, { pageNumber: page, pageSize: 20 })
      .then((res) => {
        const newData = extractListResult(res.data);
        setBlockedUsers((prev) => (page === 1 ? newData : [...prev, ...newData]));
        setBlockedMeta(res.data?.Metadata || null);
        setBlockedPage(page);
      })
      .catch(() => toast.error(t("participantsTab.loadBlockedError")))
      .finally(() => setLoadingBlocked(false));
  };

  useEffect(() => {
    loadSubPromoters();
    if (canUseBusinessAssistants) loadBusinessAssistants();
    else setLoadingBA(false);
    loadParticipants(1);
    if (isAdmin) loadBlockedUsers(1);
    if (isAdmin) loadHostRequestStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, isAdmin, canUseBusinessAssistants, eventHosts.length]);

  const hasMoreParticipants =
    participantsMeta && participants.length < (participantsMeta.TotalCount ?? participantsMeta.totalCount ?? 0);
  const hasMoreBlocked =
    blockedMeta && blockedUsers.length < (blockedMeta.TotalCount ?? blockedMeta.totalCount ?? 0);

  const handleUnblock = async (u) => {
    setUnblockingId(u.UserId);
    try {
      const res = await unblockUser(eventId, u.UserId);
      toast.success(res.data?.Message || t("participantsTab.unblockSuccess", { name: u.NickName || t("participantsTab.defaultNames.user") }));
      loadBlockedUsers(1);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("participantsTab.unblockError"));
    } finally {
      setUnblockingId(null);
    }
  };

  // Sub-Promoters can no longer be removed at all — Deactivate (via the
  // Status modal) is the only lifecycle action, so there's nothing left to
  // bulk-act on here; the per-row checkbox and bulk "Remove" for this
  // section were removed along with it (see the subPromoters Row above).
  const BULK_ACTION_CONFIG = {
    businessAssistants: {
      labelKey: "participantsTab.bulk.remove",
      danger: true,
      run: (ids) => bulkRemoveBusinessAssistants(ids.map(Number)),
      reload: loadBusinessAssistants,
    },
    participants: {
      labelKey: "participantsTab.bulk.block",
      danger: true,
      run: (ids) => bulkBlockKickParticipants(eventId, ids, { kick: false, block: true }),
      reload: () => { loadParticipants(1); if (isAdmin) loadBlockedUsers(1); },
    },
    blocked: {
      labelKey: "participantsTab.bulk.unblock",
      danger: false,
      run: (ids) => bulkUnblockUsers(eventId, ids),
      reload: () => loadBlockedUsers(1),
    },
  };

  const handleBulkAction = async () => {
    const config = BULK_ACTION_CONFIG[selection.section];
    if (!config) return;
    setBulkBusy(true);
    try {
      const res = await config.run(selectedIds);
      const results = res.data?.Result || [];
      const successCount = results.filter((r) => r.Success).length;
      const failCount = results.length - successCount;
      if (failCount === 0) toast.success(t("participantsTab.bulk.allSucceeded", { count: successCount }));
      else if (successCount === 0) toast.error(t("participantsTab.bulk.allFailed", { count: failCount }));
      else toast.warning(t("participantsTab.bulk.partialSuccess", { success: successCount, fail: failCount }));
      config.reload();
      clearSelection();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("participantsTab.actionError"));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleActionDone = () => {
    setRemoveTarget(null);
    setStatusTarget(null);
    setQrPermissionTarget(null);
    setPermissionsTarget(null);
    setAccessContactPermissionsTarget(null);
    loadSubPromoters();
    if (canUseBusinessAssistants) loadBusinessAssistants();
    loadParticipants(1);
    if (isAdmin) loadBlockedUsers(1);
    if (isAdmin) loadHostRequestStatuses();
  };

  const handleHostAction = async () => {
    if (!hostTarget) return;
    setHostActionLoading(true);
    try {
      if (hostTarget.mode === "make") {
        await sendHostRequest(eventId, hostTarget.userId);
      } else {
        await cancelHostRequest(hostTarget.requestId);
      }
      toast.success(hostTarget.mode === "make" ? t("participantsTab.hostRequestSent", { hostNoun }) : t("participantsTab.updated"));
      setHostTarget(null);
      loadParticipants(1);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("participantsTab.actionError"));
    } finally {
      setHostActionLoading(false);
    }
  };

  return (
    <div>
      <div style={s.statsRow}>
        <Card style={s.statBox}><div style={s.statValue}>{participantsMeta?.TotalCount ?? participants.length}</div><div style={s.statLabel}>{t("participantsTab.stats.participants")}</div></Card>
        <Card style={s.statBox}><div style={s.statValue}>{subPromoters.length}</div><div style={s.statLabel}>{t("participantsTab.stats.subPromoters")}</div></Card>
        {canUseBusinessAssistants && (
          <Card style={s.statBox}><div style={s.statValue}>{businessAssistants.length}</div><div style={s.statLabel}>{t("participantsTab.stats.assistants")}</div></Card>
        )}
      </div>

      {adminUser && (
        <Section title={t("participantsTab.sections.eventOwner")} icon={Crown}>
          <Row image={adminUser.BusinessDetails?.LogoUrl || adminUser.DisplayPicture} name={adminUser.BusinessDetails?.BusinessNickName || adminUser.NickName} />
        </Section>
      )}

      {eventHosts.length > 0 && (
        <Section title={t("participantsTab.sections.eventHosts")} icon={ShieldCheck}>
          {eventHosts.map((h, i) => {
            const name = h.AppUser?.BusinessDetails?.BusinessNickName || h.AppUser?.NickName;
            const request = hostRequestStatuses.find((p) => (p?.AppUser?.Id ?? p?.Id) === h.AppUser?.Id)?.AccessContactRequestStatus;
            return (
              <Row key={h.AppUser?.Id || i} image={h.AppUser?.BusinessDetails?.LogoUrl || h.AppUser?.DisplayPicture} name={name}>
                {isAdmin && request?.Id && (
                  <div style={s.rowActions}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAccessContactPermissionsTarget({ request, name })}>
                      {t("participantsTab.actions.permissions")}
                    </button>
                  </div>
                )}
              </Row>
            );
          })}
        </Section>
      )}

      <Section title={t("participantsTab.sections.subPromoters")} icon={Briefcase} loading={loadingSubPromoters} empty={subPromoters.length === 0 && !loadingSubPromoters}>
        {subPromoters.map((u) => {
          const id = u?.AppUser?.Id ?? u?.Id;
          const status = u?.SubPromoterStatus ?? 1;
          const canManageQR = Boolean(u?.CanManageQR);
          const name = getDisplayName(u, t("participantsTab.defaultNames.unnamed"));
          return (
            <Row
              key={id}
              image={getUserImage(u)}
              name={name}
              badge={<Badge variant={SUB_PROMOTER_STATUS_VARIANT[status]}>{t(`participantsTab.subPromoterStatus.${SUB_PROMOTER_STATUS_KEY[status]}`)}</Badge>}
            >
              {isAdmin && (
                <div style={s.rowActions}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setQrPermissionTarget({ id, name, chatRoomId: eventId, canManageQR })}
                    title={canManageQR ? t("participantsTab.qrPermission.enabledTitle") : t("participantsTab.qrPermission.disabledTitle")}
                  >
                    {t("participantsTab.actions.qrPermission")}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setStatusTarget({ id, name, chatRoomId: eventId, currentStatus: status })}>{t("participantsTab.actions.status")}</button>
                </div>
              )}
            </Row>
          );
        })}
      </Section>

      {canUseBusinessAssistants && (
        <Section
          title={t("participantsTab.sections.businessAssistants")}
          icon={UsersIcon}
          loading={loadingBA}
          empty={businessAssistants.length === 0 && !loadingBA}
          action={isAdmin ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowInviteBA(true)}>
              <UserPlus size={13} strokeWidth={2.5} /> {t("participantsTab.actions.invite")}
            </button>
          ) : null}
        >
          {businessAssistants.map((u) => {
            const name = getDisplayName(u, t("participantsTab.defaultNames.unnamed"));
            const id = u.AssistantId || u.Id;
            return (
              <Row
                key={id}
                image={getUserImage(u)}
                name={name}
                checked={selection.section === "businessAssistants" && !!selection.ids[id]}
                onToggleSelect={isAdmin ? () => toggleSelect("businessAssistants", id) : undefined}
              >
                {isAdmin && (
                  <div style={s.rowActions}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPermissionsTarget(u)}>{t("participantsTab.actions.permissions")}</button>
                    <button type="button" className="btn btn-danger-ghost btn-sm" onClick={() => setRemoveTarget({ type: "businessAssistant", id, name, chatRoomId: eventId })}>{t("common:actions.remove")}</button>
                  </div>
                )}
              </Row>
            );
          })}
        </Section>
      )}

      <Section title={t("participantsTab.sections.participants")} icon={UsersIcon} loading={loadingParticipants && participants.length === 0} empty={participants.length === 0 && !loadingParticipants}>
        {participants.map((p, i) => {
          const user = p?.AppUser || p;
          const name = user?.BusinessDetails?.BusinessNickName || user?.NickName || t("participantsTab.defaultNames.guest");
          const requestStatus = p?.AccessContactRequestStatus?.Status;
          const isPending = requestStatus === 0;
          const isHostAccess = requestStatus === 1 || p?.IsAccessContact;
          return (
            <Row
              key={user?.Id || i}
              image={user?.BusinessDetails?.LogoUrl || user?.DisplayPicture}
              name={name}
              checked={selection.section === "participants" && !!selection.ids[user?.Id]}
              onToggleSelect={isAdmin && user?.Id ? () => toggleSelect("participants", user.Id) : undefined}
            >
              {isAdmin && user?.Id && (
                <div style={s.rowActions}>
                  {isPending || isHostAccess ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        style={isHostAccess && !isPending ? { color: color.success500, borderColor: "#bbf7d0", background: color.success50 } : isPending ? { color: color.warning500, borderColor: "#fde68a", background: color.warning50 } : undefined}
                        onClick={() => setHostTarget({ mode: "cancel", requestId: p?.AccessContactRequestStatus?.Id, name, isPending })}
                      >
                        {isPending ? t("participantsTab.actions.pending") : hostNoun}
                      </button>
                    </>
                  ) : canMakeHost ? (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setHostTarget({ mode: "make", userId: user.Id, name })}>
                      {t("participantsTab.actions.makeHost", { hostNoun })}
                    </button>
                  ) : null}
                  <button type="button" className="btn btn-danger-ghost btn-sm" onClick={() => setRemoveTarget({ type: "participant", id: user.Id, name: user?.NickName || t("participantsTab.defaultNames.thisParticipant"), chatRoomId: eventId })}>{t("participantsTab.actions.block")}</button>
                </div>
              )}
            </Row>
          );
        })}
        {hasMoreParticipants && (
          <button type="button" className="btn btn-ghost btn-sm" style={s.loadMoreBtn} onClick={() => loadParticipants(participantsPage + 1)} disabled={loadingParticipants}>
            {loadingParticipants ? t("participantsTab.actions.loading") : t("participantsTab.actions.loadMore")}
          </button>
        )}
      </Section>

      {isAdmin && (
        <Section title={t("participantsTab.sections.blockedUsers")} icon={Ban} loading={loadingBlocked && blockedUsers.length === 0} empty={blockedUsers.length === 0 && !loadingBlocked}>
          {blockedUsers.map((u) => (
            <Row
              key={u.UserId}
              image={u.DisplayPicture}
              name={u.NickName || t("participantsTab.defaultNames.unnamed")}
              checked={selection.section === "blocked" && !!selection.ids[u.UserId]}
              onToggleSelect={() => toggleSelect("blocked", u.UserId)}
            >
              <div style={s.rowActions}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleUnblock(u)} disabled={unblockingId === u.UserId}>
                  {unblockingId === u.UserId ? t("participantsTab.actions.unblocking") : t("participantsTab.actions.unblock")}
                </button>
              </div>
            </Row>
          ))}
          {hasMoreBlocked && (
            <button type="button" className="btn btn-ghost btn-sm" style={s.loadMoreBtn} onClick={() => loadBlockedUsers(blockedPage + 1)} disabled={loadingBlocked}>
              {loadingBlocked ? t("participantsTab.actions.loading") : t("participantsTab.actions.loadMore")}
            </button>
          )}
        </Section>
      )}

      {removeTarget && (
        <RemoveParticipantModal target={removeTarget} onClose={() => setRemoveTarget(null)} onRemoved={handleActionDone} />
      )}
      {statusTarget && (
        <SubPromoterStatusModal target={statusTarget} onClose={() => setStatusTarget(null)} onUpdated={handleActionDone} />
      )}
      {qrPermissionTarget && (
        <SubPromoterQrPermissionModal target={qrPermissionTarget} onClose={() => setQrPermissionTarget(null)} onUpdated={handleActionDone} />
      )}
      {showInviteBA && (
        <InviteBusinessAssistantModal eventId={eventId} onClose={() => setShowInviteBA(false)} />
      )}
      {permissionsTarget && (
        <EditPermissionsModal assistant={permissionsTarget} onClose={() => setPermissionsTarget(null)} onUpdated={handleActionDone} />
      )}
      {accessContactPermissionsTarget && (
        <EditAccessContactPermissionsModal
          accessContact={accessContactPermissionsTarget.request}
          name={accessContactPermissionsTarget.name}
          onClose={() => setAccessContactPermissionsTarget(null)}
          onUpdated={handleActionDone}
        />
      )}
      {hostTarget && (
        <ConfirmModal
          title={
            hostTarget.mode === "make"
              ? t("participantsTab.hostConfirm.makeTitle", { hostNoun })
              : hostTarget.isPending
                ? t("participantsTab.hostConfirm.cancelTitle")
                : t("participantsTab.hostConfirm.removeTitle", { hostNoun })
          }
          message={
            hostTarget.mode === "make"
              ? t("participantsTab.hostConfirm.makeMessage", { hostNoun, name: hostTarget.name })
              : hostTarget.isPending
                ? t("participantsTab.hostConfirm.cancelMessage", { hostNoun, name: hostTarget.name })
                : t("participantsTab.hostConfirm.removeMessage", { hostNoun, name: hostTarget.name })
          }
          confirmText={
            hostTarget.mode === "make"
              ? t("participantsTab.hostConfirm.makeConfirm", { hostNoun })
              : hostTarget.isPending
                ? t("participantsTab.hostConfirm.cancelConfirm")
                : t("common:actions.remove")
          }
          danger={hostTarget.mode !== "make"}
          loading={hostActionLoading}
          onConfirm={handleHostAction}
          onCancel={() => !hostActionLoading && setHostTarget(null)}
        />
      )}

      {selectedIds.length > 0 && (
        <div style={s.bulkBar}>
          <span style={s.bulkCount}>{t("participantsTab.bulk.selectedCount", { count: selectedIds.length })}</span>
          <div style={s.bulkActions}>
            <button type="button" className="btn btn-ghost btn-sm" style={s.bulkCancelBtn} onClick={clearSelection} disabled={bulkBusy}>
              <X size={13} strokeWidth={2.5} /> {t("common:actions.cancel")}
            </button>
            <button
              type="button"
              className={`btn btn-sm ${BULK_ACTION_CONFIG[selection.section]?.danger ? "btn-danger" : "btn-primary"}`}
              onClick={handleBulkAction}
              disabled={bulkBusy}
            >
              {bulkBusy ? t("participantsTab.actions.loading") : t(BULK_ACTION_CONFIG[selection.section]?.labelKey)}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, icon: Icon, loading, empty, action, children }) => {
  const { t } = useTranslation("eventDetails");
  return (
    <Card style={s.card}>
      <div style={s.sectionHeader}>
        <h4 style={s.sectionTitle}><Icon size={15} strokeWidth={2.25} color={color.gray500} /> {title}</h4>
        {action}
      </div>
      {loading ? <Loader /> : empty ? <EmptyState text={t("participantsTab.empty")} /> : <div style={s.rowList}>{children}</div>}
    </Card>
  );
};

const Row = ({ image, name, badge, checked, onToggleSelect, children }) => (
  <div style={s.row}>
    {onToggleSelect && <Checkbox checked={!!checked} onChange={onToggleSelect} aria-label={name} />}
    <Avatar src={image} name={name} />
    <span style={s.rowName}>{name}</span>
    {badge}
    {children}
  </div>
);

const s = {
  statsRow: { display: "flex", gap: 12, marginBottom: 16 },
  statBox: { flex: 1, textAlign: "center", padding: "16px 12px" },
  statValue: { fontSize: 21, fontWeight: 800, color: color.textPrimary, letterSpacing: -0.4 },
  statLabel: { fontSize: 11, color: color.textMuted, marginTop: 3, fontWeight: 500 },
  card: { marginBottom: 16 },
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  sectionTitle: { margin: 0, fontSize: 13.5, fontWeight: 700, color: color.textPrimary, display: "flex", alignItems: "center", gap: 8 },
  rowList: { display: "flex", flexDirection: "column", gap: 12 },
  row: { display: "flex", alignItems: "center", gap: 10 },
  avatar: { width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: color.gray100 },
  avatarFallback: {
    width: 32, height: 32, borderRadius: "50%", background: color.primary50, color: color.primary600,
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0,
  },
  rowName: { fontSize: 13, color: color.textPrimary, fontWeight: 500, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowActions: { display: "flex", gap: 6, flexShrink: 0 },
  loadMoreBtn: { marginTop: 6, alignSelf: "center", display: "block", width: "100%" },
  bulkBar: {
    position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)", zIndex: 300,
    display: "flex", alignItems: "center", gap: 16, padding: "10px 12px 10px 18px",
    background: color.gray900, borderRadius: radius.pill, boxShadow: shadow.lg,
  },
  bulkCount: { fontSize: 13, fontWeight: 600, color: "#fff", whiteSpace: "nowrap" },
  bulkActions: { display: "flex", gap: 8 },
  bulkCancelBtn: { color: "#d1d5db" },
};

export default ParticipantsTab;
